export const PRICING_KEYS = [
  'ModelPrice',
  'ModelRatio',
  'CompletionRatio',
  'CacheRatio',
  'CreateCacheRatio',
  'ImageRatio',
  'AudioRatio',
  'AudioCompletionRatio',
] as const

export type PricingKey = (typeof PRICING_KEYS)[number]

export const RATIO_KEYS = PRICING_KEYS.filter((key): key is Exclude<PricingKey, 'ModelPrice'> => key !== 'ModelPrice')

export const SYNC_RATIO_TYPES = [
  'model_ratio',
  'completion_ratio',
  'cache_ratio',
  'model_price',
] as const

export type SyncRatioType = (typeof SYNC_RATIO_TYPES)[number]

export const SYNC_OPTION_KEYS = ['ModelRatio', 'CompletionRatio', 'CacheRatio', 'ModelPrice'] as const

export type SyncOptionKey = (typeof SYNC_OPTION_KEYS)[number]

export const RATIO_TYPE_TO_OPTION_KEY: Record<SyncRatioType, SyncOptionKey> = {
  model_ratio: 'ModelRatio',
  completion_ratio: 'CompletionRatio',
  cache_ratio: 'CacheRatio',
  model_price: 'ModelPrice',
}

export function ratioTypeToOptionKey(ratioType: string): SyncOptionKey | null {
  if (ratioType in RATIO_TYPE_TO_OPTION_KEY) {
    return RATIO_TYPE_TO_OPTION_KEY[ratioType as SyncRatioType]
  }
  return null
}

export function isSyncRatioType(ratioType: string): ratioType is SyncRatioType {
  return ratioType in RATIO_TYPE_TO_OPTION_KEY
}

export function getBillingCategory(ratioType: string): 'price' | 'ratio' {
  return ratioType === 'model_price' ? 'price' : 'ratio'
}

export type PricingMaps = Record<PricingKey, Record<string, number>>

export function createEmptyPricingMaps(): PricingMaps {
  return PRICING_KEYS.reduce<PricingMaps>((acc, key) => {
    acc[key] = {}
    return acc
  }, {} as PricingMaps)
}

export function clearModelRatios(maps: PricingMaps, model: string): void {
  RATIO_KEYS.forEach((key) => {
    delete maps[key][model]
  })
}

export function clearModelPrice(maps: PricingMaps, model: string): void {
  delete maps.ModelPrice[model]
}

export interface ParseOptionJSONResult {
  value: Record<string, unknown>
  error?: string
}

export function parseOptionJSONSafe(rawValue: string): ParseOptionJSONResult {
  if (!rawValue || rawValue.trim() === '') {
    return { value: {} }
  }
  try {
    const parsed = JSON.parse(rawValue) as unknown
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return { value: {}, error: '必须是 JSON 对象' }
    }
    return { value: parsed as Record<string, unknown> }
  } catch (error) {
    return {
      value: {},
      error: error instanceof Error ? error.message : 'JSON 解析失败',
    }
  }
}

export function parsePricingMapsFromOptions(
  values: Record<string, string>
): { maps: PricingMaps; errors: Record<string, string> } {
  const maps = createEmptyPricingMaps()
  const errors: Record<string, string> = {}

  PRICING_KEYS.forEach((key) => {
    const result = parseOptionJSONSafe(values[key] ?? '')
    if (result.error) {
      errors[key] = result.error
      return
    }
    Object.entries(result.value).forEach(([model, rawValue]) => {
      const num = Number(rawValue)
      if (Number.isFinite(num)) {
        maps[key][model] = num
      }
    })
  })

  return { maps, errors }
}
